import type { AppSchemasProgrammingSessionProgramPageSession } from "@classes-service-types/app-schemas-programming-session-program-page-session";
import type { EntityResponse } from "@classes-service-types/entity-response.ts";
import type { ProfileInDbWithApplications } from "@profile-service-types/profile-in-db-with-applications";
import { computed, type MaybeRefOrGetter, toValue } from "vue";

/**
 * Controls which Colleges should appear inside dropdown based on session
 * @param profile
 * @param session
 * @param universitiesFromSearchRef
 * @param selectedSchool
 */

export const useAllowedUniversities = (
  profile: MaybeRefOrGetter<ProfileInDbWithApplications>,
  session: MaybeRefOrGetter<AppSchemasProgrammingSessionProgramPageSession>,
  universitiesFromSearchRef: MaybeRefOrGetter<EntityResponse[]>,
  selectedSchool: MaybeRefOrGetter<EntityResponse>,
) => {
  const profileCollege = computed(() => structuredClone(toValue(profile)?.colleges?.[0] ?? undefined));
  const allowedUniversities = computed(() => structuredClone(toValue(session)?.allowed_participants ?? []));
  const universitiesFromSearch = computed(() => structuredClone(toValue(universitiesFromSearchRef)));
  const selectedSchoolId = computed(() => toValue(selectedSchool)?.id ?? profileCollege.value?.college_id);

  const universities = computed(() => {
    const schools = allowedUniversities.value?.length <= 0 ? universitiesFromSearch.value : allowedUniversities.value;
    return schools.map((school) => ({
      label: school?.name,
      value: school?.ope_id,
      ...school,
    }));
  });

  const currentCollegeIsValid = computed(() => {
    if (allowedUniversities.value?.length <= 0) return true;
    if (!selectedSchoolId.value) return false;
    return allowedUniversities.value?.some((school) => school?.id === selectedSchoolId.value);
  });

  const invalidCollegeFromProfile = computed(() => {
    return (
      profileCollege.value !== undefined &&
      allowedUniversities.value?.length >= 1 &&
      !allowedUniversities.value?.some((school) => school?.id === profileCollege.value?.college_id)
    );
  });

  return { allowedUniversities, universities, currentCollegeIsValid, invalidCollegeFromProfile };
};
